import { Copy, Check, Download, FileDown, ChevronDown, ChevronUp } from 'lucide-react'
import React, { useRef, useState } from 'react'
import QRCode from 'react-qr-code'
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import type { CustodyResponse } from '@/shared/types/api.types'
import { formatDate } from '@/shared/lib/utils'

interface CustodyResultPanelProps {
  result: CustodyResponse
  originalFile: File
  onReset: () => void
}

type QRPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'

export function CustodyResultPanel({ result, originalFile, onReset }: CustodyResultPanelProps) {
  const [copiedUrl, setCopiedUrl] = React.useState(false)
  const [copiedHash, setCopiedHash] = React.useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [qrPosition, setQrPosition] = useState<QRPosition>('bottom-right')
  const [showQrSection, setShowQrSection] = useState(false)
  const [showInfoSection, setShowInfoSection] = useState(false)
  const qrRef = useRef<HTMLDivElement>(null)
  const hiddenQrRef = useRef<HTMLDivElement>(null)

  const handleCopy = async (text: string, setCopied: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleDownloadPDF = () => {
    if (!result.report_pdf_base64) return

    const byteCharacters = atob(result.report_pdf_base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: 'application/pdf' })

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = result.report_filename || `custodia_${result.custody_id}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleDownloadQR = () => {
    // Get the SVG element from react-qr-code
    const svg = qrRef.current?.querySelector('svg')
    if (!svg) return

    // Convert SVG to canvas then to PNG
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      canvas.width = 300
      canvas.height = 300
      ctx?.drawImage(img, 0, 0, 300, 300)

      // Download as PNG
      const pngUrl = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      link.href = pngUrl
      link.download = `qr_custodia_${result.custody_id}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  const handleDownloadPdfWithQR = async () => {
    if (!originalFile) return

    setIsGeneratingPdf(true)

    try {
      // Read original PDF
      const fileBuffer = await originalFile.arrayBuffer()
      const pdfDoc = await PDFDocument.load(fileBuffer)

      // Get all pages
      const pages = pdfDoc.getPages()
      const lastPage = pages[pages.length - 1]
      const { width, height } = lastPage.getSize()

      // Generate QR code as PNG (use hidden ref that's always rendered)
      const svg = hiddenQrRef.current?.querySelector('svg')
      if (!svg) throw new Error('QR not found')

      const svgData = new XMLSerializer().serializeToString(svg)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      await new Promise<void>((resolve) => {
        img.onload = () => {
          canvas.width = 300
          canvas.height = 300
          ctx?.drawImage(img, 0, 0, 300, 300)
          resolve()
        }
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
      })

      // Get PNG bytes
      const pngDataUrl = canvas.toDataURL('image/png')
      const pngBase64 = pngDataUrl.split(',')[1]
      const pngBytes = Uint8Array.from(atob(pngBase64), c => c.charCodeAt(0))

      // Embed QR image
      const qrImage = await pdfDoc.embedPng(pngBytes)
      const qrSize = 40 // Size in PDF points (smaller)
      const margin = 15

      // Calculate position based on selection
      // QR is placed with space below for URL text
      let x: number, y: number
      const textSpace = 10 // Space for URL text below QR
      switch (qrPosition) {
        case 'bottom-left':
          x = margin
          y = margin + textSpace
          break
        case 'top-right':
          x = width - qrSize - margin
          y = height - qrSize - margin
          break
        case 'top-left':
          x = margin
          y = height - qrSize - margin
          break
        case 'bottom-right':
        default:
          x = width - qrSize - margin
          y = margin + textSpace
          break
      }

      // Draw QR code
      lastPage.drawImage(qrImage, {
        x,
        y,
        width: qrSize,
        height: qrSize,
      })

      // Add URL text below QR (always below, in 2 lines)
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
      const fontSize = 3
      // Shorten URL and split into 2 lines
      const shortUrl = result.verification_url.replace('http://', '').replace('https://', '')
      const midPoint = Math.ceil(shortUrl.length / 2)
      const line1 = shortUrl.substring(0, midPoint)
      const line2 = shortUrl.substring(midPoint)

      // Line 1 (closer to QR)
      lastPage.drawText(line1, {
        x: x,
        y: y - 5,
        size: fontSize,
        font,
        color: rgb(0.3, 0.3, 0.3),
      })

      // Line 2
      lastPage.drawText(line2, {
        x: x,
        y: y - 9,
        size: fontSize,
        font,
        color: rgb(0.3, 0.3, 0.3),
      })

      // Save modified PDF
      const modifiedPdfBytes = await pdfDoc.save()
      const blob = new Blob([modifiedPdfBytes as BlobPart], { type: 'application/pdf' })

      // Download
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const originalName = originalFile.name.replace('.pdf', '')
      link.download = `${originalName}_con_qr.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

    } catch (err) {
      console.error('Error generating PDF with QR:', err)
      alert('Error al generar el PDF. El archivo puede estar protegido o dañado.')
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      {/* Success Card */}
      <div className="card border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/30">
        <div className="flex items-start gap-4">
          <div className="text-5xl">✅</div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-300">
              Custodia Registrada
            </h2>
            <p className="text-blue-700 dark:text-blue-200 mt-2">
              El documento ha sido registrado exitosamente. Guardá el código QR y la constancia PDF.
            </p>
          </div>
        </div>
      </div>

      {/* Document Info - Collapsible */}
      <div className="card">
        <button
          type="button"
          onClick={() => setShowInfoSection(!showInfoSection)}
          className="w-full flex items-center justify-between text-left"
        >
          <h3 className="text-xl font-bold text-legal-900 dark:text-white">
            Información del Registro
          </h3>
          {showInfoSection ? (
            <ChevronUp className="w-5 h-5 text-legal-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-legal-500" />
          )}
        </button>

        {showInfoSection && (
          <dl className="space-y-3 mt-4 pt-4 border-t border-legal-200 dark:border-legal-600">
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                ID de Custodia
              </dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100 font-mono text-sm break-all bg-legal-100 dark:bg-legal-700 p-2 rounded">
                {result.custody_id}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                Fecha de Registro
              </dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100">
                {formatDate(result.custody_timestamp)}
              </dd>
            </div>

            {result.original_filename && (
              <div>
                <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                  Archivo Original
                </dt>
                <dd className="mt-1 text-legal-900 dark:text-legal-100">
                  {result.original_filename}
                </dd>
              </div>
            )}

            {result.document_title && (
              <div>
                <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                  Título
                </dt>
                <dd className="mt-1 text-legal-900 dark:text-legal-100">
                  {result.document_title}
                </dd>
              </div>
            )}

            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                Hash del Documento ({result.hash_algorithm})
              </dt>
              <dd className="mt-1 flex items-center gap-2">
                <code className="flex-1 text-legal-900 dark:text-legal-100 font-mono text-xs break-all bg-legal-100 dark:bg-legal-700 p-2 rounded">
                  {result.file_hash}
                </code>
                <button
                  onClick={() => handleCopy(result.file_hash, setCopiedHash)}
                  className="btn-secondary p-2 flex-shrink-0"
                  title="Copiar hash"
                >
                  {copiedHash ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </dd>
            </div>
          </dl>
        )}
      </div>

      {/* QR Code - Collapsible */}
      <div className="card border-l-4 border-cyan-500">
        <button
          type="button"
          onClick={() => setShowQrSection(!showQrSection)}
          className="w-full flex items-center justify-between text-left"
        >
          <h3 className="text-xl font-bold text-legal-900 dark:text-white">
            Código QR de Validación
          </h3>
          {showQrSection ? (
            <ChevronUp className="w-5 h-5 text-legal-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-legal-500" />
          )}
        </button>

        {showQrSection && (
          <>
            <div className="flex flex-col items-center py-4 mt-4 bg-white dark:bg-legal-800 rounded-lg border-2 border-dashed border-legal-300 dark:border-legal-600">
              <p className="text-sm font-medium text-legal-500 dark:text-legal-400 mb-3">
                Escaneá para validar públicamente
              </p>
              <div ref={qrRef} className="bg-white p-4 rounded-lg">
                <QRCode
                  value={result.verification_url}
                  size={200}
                  level="M"
                />
              </div>
              <button
                onClick={handleDownloadQR}
                className="mt-3 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                <Download className="w-4 h-4" />
                Descargar QR
              </button>
            </div>

            {/* URL with copy button */}
            <div className="mt-4">
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400 mb-2">
                URL de Validación Pública
              </dt>
              <dd className="flex items-center gap-2">
                <a
                  href={result.verification_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline text-sm break-all flex-1"
                >
                  {result.verification_url}
                </a>
                <button
                  onClick={() => handleCopy(result.verification_url, setCopiedUrl)}
                  className="btn-secondary p-2 flex-shrink-0"
                  title="Copiar URL"
                >
                  {copiedUrl ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </dd>
            </div>
          </>
        )}
      </div>

      {/* Hidden QR for PDF generation (always rendered but hidden) */}
      <div className="hidden">
        <div ref={hiddenQrRef}>
          <QRCode
            value={result.verification_url}
            size={200}
            level="M"
          />
        </div>
      </div>

      {/* Insert QR into PDF */}
      <div className="card border-l-4 border-green-500">
        <h3 className="text-xl font-bold text-legal-900 dark:text-white mb-4">
          Insertar QR en Documento Original
        </h3>
        <p className="text-sm text-legal-600 dark:text-legal-300 mb-4">
          Agregá el código QR a tu documento PDF original para que cualquiera pueda verificarlo.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-2">
              Posición del QR en la última página:
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'bottom-right', label: 'Abajo derecha' },
                { value: 'bottom-left', label: 'Abajo izquierda' },
                { value: 'top-right', label: 'Arriba derecha' },
                { value: 'top-left', label: 'Arriba izquierda' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setQrPosition(option.value as QRPosition)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    qrPosition === option.value
                      ? 'bg-green-100 dark:bg-green-900/50 border-green-500 text-green-700 dark:text-green-300'
                      : 'bg-white dark:bg-legal-700 border-legal-300 dark:border-legal-600 text-legal-700 dark:text-legal-300 hover:bg-legal-50 dark:hover:bg-legal-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleDownloadPdfWithQR}
            disabled={isGeneratingPdf}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {isGeneratingPdf ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generando PDF...
              </>
            ) : (
              <>
                <FileDown className="w-5 h-5" />
                Descargar PDF Original con QR
              </>
            )}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        {result.report_pdf_base64 && (
          <button
            onClick={handleDownloadPDF}
            className="btn-secondary flex-1 flex items-center justify-center gap-2"
          >
            <Download className="w-5 h-5" />
            Descargar Constancia PDF
          </button>
        )}
        <button onClick={onReset} className="btn-secondary flex-1">
          Registrar Otro Documento
        </button>
      </div>

      {/* Instructions */}
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-600 rounded-lg">
        <h3 className="font-semibold text-yellow-900 dark:text-yellow-300 mb-2">
          Importante
        </h3>
        <ul className="text-sm text-yellow-800 dark:text-yellow-200 space-y-1 list-disc list-inside">
          <li>Guardá el PDF de constancia y el código QR</li>
          <li>Conservá el documento original (no lo subimos)</li>
          <li>El código QR puede incluirse en el documento para que cualquiera pueda verificarlo</li>
        </ul>
      </div>
    </div>
  )
}

export default CustodyResultPanel
